'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFeature } from '@/hooks/use-feature';
import { useRequireContract } from '@/shared/hooks/use-require-contract';
import { canPostInCommunity, canUploadContent } from '@/shared/lib/auth/permissions';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { ContractGateSource } from '@/shared/stores/contract-gate-store';
import { GraduationCap, ListVideo, PenLine, Plus, Video } from 'lucide-react';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useRouter } from 'next/navigation';

interface CreateMenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  contractSource: ContractGateSource;
  action: (router: AppRouterInstance) => void;
}

const CREATE_MENU_ITEMS: CreateMenuItem[] = [
  {
    id: 'upload-video',
    label: 'Upload video',
    icon: Video,
    contractSource: 'create_menu_upload',
    action: (router) => router.push('/studio/content?action=upload', { scroll: false }),
  },
  {
    id: 'new-series',
    label: 'New series',
    icon: ListVideo,
    contractSource: 'create_menu_series',
    action: (router) => router.push('/studio/series?action=create', { scroll: false }),
  },
  {
    id: 'new-course',
    label: 'New course',
    icon: GraduationCap,
    contractSource: 'create_menu_course',
    action: (router) => router.push('/studio/courses?action=create', { scroll: false }),
  },
  {
    id: 'create-post',
    label: 'Create post',
    icon: PenLine,
    contractSource: 'create_menu_post',
    action: (router) => router.push('/compose/post', { scroll: false }),
  },
];

// Self-gates on creator status — don't rely solely on parent auth checks
export function CreateMenu() {
  const canUpload = useAuthStore((s) => canUploadContent(s.user));
  const canPost = useAuthStore((s) => canPostInCommunity(s.user));
  const studioSeriesEnabled = useFeature('STUDIO_SERIES');
  const studioCoursesEnabled = useFeature('STUDIO_COURSES');
  const router = useRouter();
  const ensureContract = useRequireContract();

  if (!canUpload) return null;

  const menuItems = CREATE_MENU_ITEMS.filter((item) => {
    if (item.id === 'create-post') return canPost;
    if (item.id === 'new-series') return studioSeriesEnabled;
    if (item.id === 'new-course') return studioCoursesEnabled;
    return true;
  });

  return (
    <div className="hidden sm:flex">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex h-10 items-center gap-2 rounded-full bg-red-primary px-4 text-sm font-medium text-white transition-colors hover:bg-red-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-primary/30"
            aria-label="Create content"
          >
            <Plus className="size-5" />
            Create
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          sideOffset={8}
          className="w-56 rounded border-0 bg-bg-card shadow-xl z-9999 p-0"
        >
          {menuItems.map((item) => (
            <DropdownMenuItem
              key={item.id}
              onClick={() => ensureContract(() => item.action(router), item.contractSource)}
              className="no-focus-outline flex items-center gap-3 rounded-none px-3 py-2 text-sm hover:bg-white/10 transition-colors"
            >
              <item.icon className="h-5 w-5 text-white/70" />
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
